import { useState, useMemo } from "react";
import { PASSIVES, PASSIVE_SOURCES } from "@/data/passives";
import { Sparkles, Search, ArrowUpDown, TrendingUp, TrendingDown, Filter } from "lucide-react";

function rankColor(rank) {
  if (rank >= 3) return "#22C55E";
  if (rank > 0) return "#84CC16";
  if (rank === 0) return "#94A3B8";
  if (rank > -3) return "#F59E0B";
  return "#EF4444";
}

function RankBadge({ rank }) {
  const c = rankColor(rank);
  const sign = rank > 0 ? `+${rank}` : `${rank}`;
  return (
    <span
      data-testid="passive-rank"
      className="inline-flex items-center justify-center w-9 h-9 rounded-md font-head font-bold text-sm shrink-0"
      style={{ color: c, background: `${c}1a`, border: `1px solid ${c}55` }}
    >
      {sign}
    </span>
  );
}

const SORTS = [
  { key: "rank-desc", label: "Rang (fort → faible)", icon: TrendingDown },
  { key: "rank-asc", label: "Rang (faible → fort)", icon: TrendingUp },
  { key: "name", label: "Nom (A → Z)", icon: ArrowUpDown },
];

export default function Passives() {
  const [q, setQ] = useState("");
  const [source, setSource] = useState("all");
  const [polarity, setPolarity] = useState("all");
  const [sort, setSort] = useState("rank-desc");

  const results = useMemo(() => {
    const term = q.trim().toLowerCase();
    let list = PASSIVES.filter((p) => {
      if (term && !(p.name.toLowerCase().includes(term) || (p.desc || "").toLowerCase().includes(term))) return false;
      if (source !== "all" && !(p.sources || []).includes(source)) return false;
      if (polarity === "pos" && p.rank <= 0) return false;
      if (polarity === "neg" && p.rank >= 0) return false;
      return true;
    });
    list = [...list].sort((a, b) => {
      if (sort === "name") return a.name.localeCompare(b.name);
      if (sort === "rank-asc") return a.rank - b.rank || a.name.localeCompare(b.name);
      return b.rank - a.rank || a.name.localeCompare(b.name);
    });
    return list;
  }, [q, source, polarity, sort]);

  return (
    <div className="fade-up max-w-4xl space-y-6" data-testid="passives-page">
      <div>
        <div className="text-xs font-bold tracking-[0.2em] uppercase text-primary/80">Base de données</div>
        <h1 className="font-head text-3xl sm:text-4xl font-bold tracking-tight flex items-center gap-2">
          <Sparkles className="w-7 h-7 text-primary" /> Compétences passives
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          {PASSIVES.length} passifs de Palworld 1.0 — recherche par nom ou effet, filtre par source et trie par rang.
        </p>
      </div>

      {/* Controls */}
      <div className="space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            data-testid="passive-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher un passif (nom ou effet)…"
            className="w-full rounded-lg border border-border bg-[hsl(var(--surface))] pl-9 pr-3 py-2.5 text-sm outline-none focus:border-primary/50"
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Source filter */}
          <div className="flex items-center gap-1 rounded-lg border border-border bg-[hsl(var(--surface))] p-1 flex-wrap">
            <span className="px-2 text-[10px] font-bold uppercase tracking-wider text-muted-foreground inline-flex items-center gap-1"><Filter className="w-3 h-3" /> Source</span>
            <button data-testid="passive-source-all" onClick={() => setSource("all")} className={`rounded-md px-2.5 py-1.5 text-xs font-semibold transition-colors ${source === "all" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>Tous</button>
            {Object.entries(PASSIVE_SOURCES).map(([k, m]) => (
              <button key={k} data-testid={`passive-source-${k}`} onClick={() => setSource(k)} className={`rounded-md px-2.5 py-1.5 text-xs font-semibold transition-colors ${source === k ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>{m.label}</button>
            ))}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Polarity */}
          <div className="flex items-center gap-1 rounded-lg border border-border bg-[hsl(var(--surface))] p-1">
            <button data-testid="passive-polarity-all" onClick={() => setPolarity("all")} className={`rounded-md px-2.5 py-1.5 text-xs font-semibold transition-colors ${polarity === "all" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>Tous</button>
            <button data-testid="passive-polarity-pos" onClick={() => setPolarity("pos")} className={`inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs font-semibold transition-colors ${polarity === "pos" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}><TrendingUp className="w-3.5 h-3.5" /> Positifs</button>
            <button data-testid="passive-polarity-neg" onClick={() => setPolarity("neg")} className={`inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs font-semibold transition-colors ${polarity === "neg" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}><TrendingDown className="w-3.5 h-3.5" /> Négatifs</button>
          </div>
          {/* Sort */}
          <div className="flex items-center gap-1 rounded-lg border border-border bg-[hsl(var(--surface))] p-1 flex-wrap">
            <span className="px-2 text-[10px] font-bold uppercase tracking-wider text-muted-foreground inline-flex items-center gap-1"><ArrowUpDown className="w-3 h-3" /> Tri</span>
            {SORTS.map((s) => (
              <button key={s.key} data-testid={`passive-sort-${s.key}`} onClick={() => setSort(s.key)} className={`rounded-md px-2.5 py-1.5 text-xs font-semibold transition-colors ${sort === s.key ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>{s.label}</button>
            ))}
          </div>
        </div>
      </div>

      <div className="text-xs text-muted-foreground" data-testid="passive-count">{results.length} passif{results.length > 1 ? "s" : ""}</div>

      {/* List */}
      {results.length === 0 ? (
        <div className="rounded-lg border border-border bg-[hsl(var(--surface))] p-12 text-center text-sm text-muted-foreground" data-testid="passives-empty">
          <Sparkles className="w-8 h-8 mx-auto mb-2 opacity-40" />
          Aucun passif ne correspond à ta recherche.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5" data-testid="passives-list">
          {results.map((p) => (
            <div key={p.id} data-testid={`passive-${p.id}`} className="rounded-lg border border-border bg-[hsl(var(--surface))] p-3 flex gap-3 hover:border-primary/40 transition-colors">
              <RankBadge rank={p.rank} />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-head font-bold text-sm">{p.name}</span>
                  {(p.sources || []).map((s) => {
                    const m = PASSIVE_SOURCES[s];
                    if (!m) return null;
                    return (
                      <span key={s} className="inline-flex items-center rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide" style={{ color: m.color, background: `${m.color}1a`, border: `1px solid ${m.color}55` }}>
                        {m.label}
                      </span>
                    );
                  })}
                </div>
                {p.desc && <p className="mt-1 text-xs text-slate-300 whitespace-pre-line leading-relaxed">{p.desc}</p>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
